import Modal from '../components/modal';
import { getVideoEmbedURL } from './video-url';

function parseWebVTT(vttText) {
	return vttText
		.replace(/^\uFEFF?WEBVTT[^\n]*\n/, '')
		.trim()
		.split(/\n{2,}/)
		.map((block) => {
			const lines = block.trim().split('\n');
			const timingIndex = lines.findIndex((line) => line.includes('-->'));

			if (timingIndex === -1) {
				return null;
			}

			const start = lines[timingIndex].split('-->')[0].trim();
			const text = lines.slice(timingIndex + 1).join(' ').replace(/<[^>]+>/g, '').trim();

			const timeParts = start.replace(',', '.').split(':').map(Number);
			const seconds = timeParts.reduce((total, part) => (total * 60) + part, 0);

			return text ? { start, seconds, text } : null;
		})
		.filter(Boolean);
}

function seekModalVideo(seconds) {
	const media = document.querySelector('.modal__video-embed');

	if (!media) {
		return;
	}

	if (media.tagName.toLowerCase() === 'video') {
		media.currentTime = seconds;
		media.play();
		return;
	}

	if (media.src.includes('youtube.com')) {
		media.contentWindow?.postMessage(JSON.stringify({
			event: 'command',
			func: 'seekTo',
			args: [seconds, true]
		}), '*');
		media.contentWindow?.postMessage(JSON.stringify({
			event: 'command',
			func: 'playVideo',
			args: []
		}), '*');
	} else if (media.src.includes('vimeo.com')) {
		media.contentWindow?.postMessage({ method: 'setCurrentTime', value: seconds }, '*');
		media.contentWindow?.postMessage({ method: 'play' }, '*');
	}
}

function loadVideoTranscript(captionsURL) {
	const transcriptList = document.querySelector('[data-video-transcript-list]');

	if (!captionsURL || !transcriptList) {
		return;
	}

	fetch(captionsURL)
		.then((response) => {
			if (!response.ok) {
				throw new Error('Unable to load video transcript.');
			}

			return response.text();
		})
		.then((vttText) => {
			const cues = parseWebVTT(vttText);
			transcriptList.textContent = '';

			cues.forEach((cue) => {
				const item = document.createElement('li');
				const timestamp = document.createElement('button');
				const cueText = document.createElement('span');

				timestamp.type = 'button';
				timestamp.className = 'modal__video-transcript-time';
				timestamp.textContent = cue.start;
				timestamp.setAttribute('aria-label', `Play video from ${cue.start}`);
				timestamp.addEventListener('click', () => seekModalVideo(cue.seconds));
				cueText.textContent = cue.text;
				item.append(timestamp, cueText);
				transcriptList.appendChild(item);
			});
		})
		.catch(() => {
			transcriptList.textContent = 'The video transcript could not be loaded.';
		});
}

/**
 * Show a video modal with the specified video URL
 *
 * @param {string} videoUrl The video URL
 * @param {Object} options Video captions options
 */
export function showVideoModal(videoUrl, options = {}) {
	if (!videoUrl) {
		return;
	}

	const embedUrl = getVideoEmbedURL(videoUrl);
	const { captionsURL = '', transcriptURL = '' } = options;

	Modal.show('', {
		modalClass: 'modal modal-video',
		ariaLabel: 'Video'
	});

	const isDirectURL = embedUrl.toLowerCase().indexOf('.mp4') !== -1;
	const captionsMarkup = captionsURL
		? `<track kind="captions" src="${captionsURL}" srclang="en" label="English" default>`
		: '';
	const transcriptMarkup = transcriptURL
		? `
			<details class="modal__video-transcript">
				<summary>Video transcript</summary>
				<ol class="modal__video-transcript-list" data-video-transcript-list>
					<li>Loading transcript…</li>
				</ol>
			</details>
		`
		: '';
	const videoMarkup = isDirectURL
		? `
			<video class="modal__video-embed" src="${embedUrl}" controls autoplay>
				${captionsMarkup}
			</video>
		`
		: `
			<iframe class="modal__video-embed"
				src="${embedUrl}"
				width="640"
				height="360"
				frameborder="0"
				allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
				allowfullscreen>
			</iframe>
		`;
	const templateMarkup = `
		<div class="modal__video-container">
			${videoMarkup}
		</div>
		${transcriptMarkup}
	`;

	// set the content to begin the iframe load but do not remove the loading animation
	Modal.setContent(templateMarkup, false);
	loadVideoTranscript(transcriptURL);

	const iframeEl = document.querySelector('.modal__video-embed');

	// wait for the 'load' event to fire before removing the loading animation
	iframeEl.addEventListener(isDirectURL ? 'loadeddata' : 'load', () => {
		Modal.setLoading(false);
	});
}
